import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { HqApiService } from '../../core/services/headquarters/hq-api.service';
import { DashboardApiService } from '../../core/services/dashboard/dashboard-api.service';
import { LookupDataService } from '../../core/services/lookup-data.service';
import { GoogleMapsLocationService } from '../../core/services/google-maps-location/google-maps-location.service';
import { filtersGridClass } from './filters-grid';
import { IconComponent } from '../icons/icon.component';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';

/** Keys of available filter controls. Pass a subset via [visibleFilters] to show only those. */
export type FilterKey = 'zone' | 'hq' | 'state' | 'city' | 'mr' | 'period' | 'status';

/** All available filter keys, used as the default set */
const ALL_FILTERS: FilterKey[] = ['zone', 'hq', 'state', 'city', 'mr', 'period', 'status'];

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, NgSelectModule],
  template: `
    <div class="relative bg-white p-5 border border-[#DFE8E9] rounded-xl shadow-[0_1px_3px_rgba(24,60,64,0.04)] mb-4 fade-in" style="width: 100% !important;">
      <button *ngIf="hasActiveFilters" type="button"
        class="absolute top-2 right-4 text-xs font-medium text-[#62777E] hover:text-red-500 hover:underline cursor-pointer"
        (click)="resetFilters()">
        Clear filters
      </button>
      <div class="grid gap-4 w-full"
           [ngClass]="gridColClass">

        <!-- Zone Filter -->
        <div *ngIf="show('zone')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">Zone</label>
          <ng-select class="ng-select-sm"
            [items]="zoneOptions"
            [(ngModel)]="filters.zone"
            placeholder="All Zones"
            [clearable]="false"
            appendTo="body"
            (change)="onFilterChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No zones found {{ searchTerm ? 'for "' + searchTerm + '"' : '' }}
              </div>
            </ng-template>
          </ng-select>
        </div>

        <!-- HQ Filter -->
        <div *ngIf="show('hq')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">HQ</label>
          <ng-select class="ng-select-sm"
            [items]="hqOptions"
            bindLabel="name"
            bindValue="name"
            [(ngModel)]="filters.hq"
            placeholder="All HQs"
            [clearable]="false"
            appendTo="body"
            (open)="onHqOpen()"
            (search)="onHqSearch($event)"
            (clear)="onHqClear()"
            (change)="onFilterChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No HQs found {{ searchTerm ? 'for "' + searchTerm + '"' : '' }}
              </div>
            </ng-template>
          </ng-select>
        </div>

        <!-- State Filter -->
        <div *ngIf="show('state')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">State</label>
          <ng-select class="ng-select-sm"
            [items]="stateOptions"
            [(ngModel)]="filters.state"
            placeholder="All States"
            [clearable]="false"
            appendTo="body"
            (open)="onStateOpen()"
            (change)="onStateFilterChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No states found {{ searchTerm ? 'for "' + searchTerm + '"' : '' }}
              </div>
            </ng-template>
          </ng-select>
        </div>

        <!-- City Filter -->
        <div *ngIf="show('city')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">City</label>
          <ng-select class="ng-select-sm"
            [items]="cityOptions"
            [(ngModel)]="filters.city"
            placeholder="All Cities"
            [clearable]="false"
            appendTo="body"
            (change)="onFilterChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                No cities found {{ searchTerm ? 'for "' + searchTerm + '"' : '' }}
              </div>
            </ng-template>
          </ng-select>
        </div>

        <!-- MR (Telecaller) Filter -->
        <div *ngIf="show('mr')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">MR (Telecaller)</label>
          <ng-select class="ng-select-sm"
            [items]="mrOptions"
            bindLabel="name"
            bindValue="id"
            [(ngModel)]="filters.mr"
            placeholder="All MRs"
            [clearable]="false"
            appendTo="body"
            (open)="onMrOpen()"
            (change)="onFilterChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No MRs found {{ searchTerm ? 'for "' + searchTerm + '"' : '' }}
              </div>
            </ng-template>
          </ng-select>
        </div>

        <!-- Status Filter -->
        <div *ngIf="show('status')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">{{ statusLabel }}</label>
          <ng-select class="ng-select-sm"
            [items]="statusOptions"
            bindLabel="label"
            bindValue="value"
            [(ngModel)]="filters.status"
            [placeholder]="statusPlaceholder"
            [multiple]="statusMultiple"
            [closeOnSelect]="!statusMultiple"
            [clearable]="statusMultiple"
            appendTo="body"
            (change)="onFilterChange()"
          >
            <!-- Multi-select: the input only shows a count, the chips live below -->
            <ng-template ng-multi-label-tmp let-items="items">
              <span class="text-[13px] text-[#182E36] pl-1">{{ items.length }} selected</span>
            </ng-template>
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No options found
              </div>
            </ng-template>
          </ng-select>

          <!-- Selected statuses as removable chips -->
          <div *ngIf="statusMultiple && selectedStatusItems.length" class="flex flex-wrap gap-1.5 mt-2">
            <span *ngFor="let item of selectedStatusItems"
              class="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-medium pl-2.5 pr-1.5 py-0.5">
              {{ item.label }}
              <button type="button" class="hover:bg-teal-100 rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                [attr.aria-label]="'Remove ' + item.label" (click)="removeStatus(item.value)">×</button>
            </span>
          </div>
        </div>

        <!-- Period Filter -->
        <div *ngIf="show('period')">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">Period</label>
          <ng-select class="ng-select-sm"
            [items]="periodOptions"
            bindLabel="label"
            bindValue="value"
            [(ngModel)]="filters.period"
            [placeholder]="periodPlaceholder"
            [clearable]="false"
            appendTo="body"
            (change)="onPeriodChange()"
          >
            <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
              <div class="p-3 text-center text-xs text-slate-400">
                <div class="mb-1 flex justify-center">
                  <app-icon name="alert-circle" [size]="16" class="opacity-60 text-slate-400"></app-icon>
                </div>
                No options found
              </div>
            </ng-template>
          </ng-select>
        </div>
      </div>

      <!-- Custom Date Range (only when period filter is visible and set to custom) -->
      <div *ngIf="show('period') && filters.period === 'custom'"
        class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-50">
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Start Date</label>
          <input type="date" class="input input-sm input-block bg-gray-50/50 border-gray-200 w-full"
            style="height: 40px;"
            [max]="filters.end_date"
            [(ngModel)]="filters.start_date" (change)="onFilterChange()" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">End Date</label>
          <input type="date" class="input input-sm input-block bg-gray-50/50 border-gray-200 w-full"
            style="height: 40px;"
            [min]="filters.start_date"
            [(ngModel)]="filters.end_date" (change)="onFilterChange()" />
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    ::ng-deep app-filters .ng-select .ng-placeholder {
      color: #94a3b8 !important;
      font-size: 13px !important;
    }
    ::ng-deep app-filters .ng-select .ng-value {
      font-size: 13px !important;
      color: #334155 !important;
    }
    ::ng-deep app-filters .ng-select .ng-arrow-wrapper .ng-arrow {
      border-right-color: #94a3b8 !important;
      border-bottom-color: #94a3b8 !important;
    }
    ::ng-deep app-filters .ng-select.ng-select-opened .ng-arrow-wrapper .ng-arrow {
      border-right-color: #3CB4A6 !important;
      border-bottom-color: #3CB4A6 !important;
    }
    ::ng-deep .ng-dropdown-panel {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03) !important;
      overflow: hidden !important;
      margin-top: 4px !important;
      animation: dropdownFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      transform-origin: top;
    }
    @keyframes dropdownFadeIn {
      from {
        opacity: 0;
        transform: translateY(-8px) scaleY(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scaleY(1);
      }
    }
    ::ng-deep .ng-dropdown-panel .ng-dropdown-panel-items {
      max-height: 200px !important;
    }
    ::ng-deep .ng-dropdown-panel .ng-option {
      padding: 8px 12px !important;
      font-size: 13px !important;
      color: #334155 !important;
      transition: background-color 0.15s ease;
    }
    ::ng-deep .ng-dropdown-panel .ng-option.ng-option-marked {
      background-color: #f1f5f9 !important;
    }
    ::ng-deep .ng-dropdown-panel .ng-option.ng-option-selected {
      background-color: rgba(60, 180, 166, 0.1) !important;
      color: #3CB4A6 !important;
      font-weight: 500 !important;
    }
  `]
})
export class FiltersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filters: any = {
    zone: null,
    hq: null,
    state: null,
    city: null,
    mr: null,
    period: null,
    status: null,
    start_date: null,
    end_date: null
  };

  @Input() statusOptions: any[] = [];
  @Input() statusLabel: string = 'Status';
  @Input() statusPlaceholder: string = 'All Statuses';
  /** Allow selecting several statuses; filters.status then holds an array (empty = all). */
  @Input() statusMultiple: boolean = false;
  @Input() periodPlaceholder: string = 'All Periods';

  /**
   * Which filter controls to show. Defaults to all.
   * Example: [visibleFilters]="['hq', 'state', 'city']"
   */
  @Input() visibleFilters: FilterKey[] = [...ALL_FILTERS];

  /**
   * Restricts the HQ dropdown to only these HQ ids (e.g. a Sales Executive's own
   * HQs — a user can belong to more than one). Null/omitted shows every HQ.
   */
  @Input() allowedHqIds: string[] | null = null;

  @Output() filtersChange = new EventEmitter<any>();
  @Output() filterChange = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  @ViewChildren(NgSelectComponent) private selects!: QueryList<NgSelectComponent>;

  /** Dropdown panels are appended to body, so close them when the page scrolls instead of leaving them floating. */
  private onScroll = (event: Event) => {
    if ((event.target as HTMLElement | null)?.closest?.('.ng-dropdown-panel')) return;
    this.selects?.forEach(sel => { if (sel.isOpen()) sel.close(); });
  };

  zoneOptions = ['Select All', 'North', 'East', 'West', 'South'];
  cityOptions: string[] = ['Select All'];
  private apiPeriods: string[] | null = null;
  hqOptions: any[] = [{ name: 'Select All', id: null }];
  mrOptions: any[] = [{ name: 'Select All', id: null }];
  stateOptions: string[] = ['Select All'];

  periodOptions: any[] = [];

  private updatePeriodOptions() {
    const labels: Record<string, string> = {
      current_month: 'Current Month',
      last_month: 'Last Month',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      this_year: 'This Year'
    };
    const values = (this.apiPeriods ?? ['last_month', 'last_3_months', 'last_6_months', 'this_year'])
      .filter(v => v !== 'all' && labels[v]);
    this.periodOptions = [
      { label: this.periodPlaceholder, value: 'all' },
      ...values.map(v => ({ label: labels[v], value: v })),
      { label: 'Custom Range', value: 'custom' }
    ];
  }

  constructor(
    private hqService: HqApiService,
    private lookupService: LookupDataService,
    private dashboardApi: DashboardApiService,
    private googleMapsLocationService: GoogleMapsLocationService
  ) { }

  ngOnInit() {
    this.updatePeriodOptions();
    this.normalizeFilters();
    window.addEventListener('scroll', this.onScroll, true);
    this.loadDashboardFilters();
  }

  /** Populates zone/state/city/period options from GET /dashboard/filters; static defaults stay if it fails. */
  private loadDashboardFilters() {
    this.dashboardApi.getFilterOptions().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.zones?.length) this.zoneOptions = ['Select All', ...res.zones];
        if (res.states?.length) this.stateOptions = ['Select All', ...res.states];
        if (res.cities?.length) this.cityOptions = ['Select All', ...res.cities];
        if (res.periods?.length) {
          this.apiPeriods = res.periods;
          this.updatePeriodOptions();
        }
        if (!this.allowedHqIds?.length && res.headquarters?.length) {
          this.hqOptions = [{ name: 'Select All', id: null }, ...res.headquarters.map(name => ({ name, id: name }))];
        }
        const mrs = (res.mrs || []).filter(m => m.name);
        if (mrs.length) this.mrOptions = [{ name: 'Select All', id: null }, ...mrs];
      },
      error: () => { /* keep fallback options */ }
    });
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScroll, true);
    if (this.cityTimer) clearTimeout(this.cityTimer);
    if (this.hqSearchTimer) clearTimeout(this.hqSearchTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['periodPlaceholder']) {
      this.updatePeriodOptions();
    }
    if (changes['filters']) {
      this.normalizeFilters();
    }
    // Only react to allowedHqIds change if HQ filter is actually shown
    if (changes['allowedHqIds'] && !changes['allowedHqIds'].isFirstChange() && this.show('hq')) {
      if (this.allowedHqIds?.length) {
        this.fetchAllowedHqs();
      } else {
        this.fetchHqs('');
      }
    }
  }

  /** Options currently selected in a multi-select status filter (for the chip row). */
  get selectedStatusItems(): { label: string; value: any }[] {
    const selected = Array.isArray(this.filters?.status) ? this.filters.status : [];
    return this.statusOptions.filter(o => selected.includes(o.value));
  }

  removeStatus(value: any): void {
    this.filters.status = (this.filters.status as any[]).filter(v => v !== value);
    this.onFilterChange();
  }

  normalizeFilters() {
    if (this.filters) {
      if (this.filters.zone === '' || this.filters.zone === 'Select All') this.filters.zone = null;
      if (this.filters.hq === '' || this.filters.hq === 'Select All') {
        this.filters.hq = null;
      } else if (this.filters.hq && !this.hqOptions.some(h => h.name === this.filters.hq)) {
        this.hqOptions = [{ name: 'Select All', id: null }, { name: this.filters.hq, id: this.filters.hq }];
      }
      if (this.filters.state === '' || this.filters.state === 'Select All') {
        this.filters.state = null;
      } else if (this.filters.state && !this.stateOptions.includes(this.filters.state)) {
        this.stateOptions = ['Select All', this.filters.state];
      }
      if (this.filters.mr === '' || this.filters.mr === 'Select All') {
        this.filters.mr = null;
      } else if (this.filters.mr && !this.mrOptions.some(m => m.id === this.filters.mr)) {
        this.mrOptions = [{ name: 'Select All', id: null }, { name: this.filters.mr, id: this.filters.mr }];
      }
      if (this.filters.period === '' || this.filters.period === 'all') this.filters.period = null;
      if (this.filters.status === '' || this.filters.status === 'Select All' ||
        (Array.isArray(this.filters.status) && !this.filters.status.length)) this.filters.status = null;
      if (this.filters.city === '' || this.filters.city === 'Select All') this.filters.city = null;
      if (this.filters.start_date === '') this.filters.start_date = null;
      if (this.filters.end_date === '') this.filters.end_date = null;
    }
  }

  get hasActiveFilters(): boolean {
    if (!this.filters) return false;
    return !!(
      (this.filters.zone && this.filters.zone !== 'Select All') ||
      (this.filters.hq && this.filters.hq !== 'Select All') ||
      (this.filters.state && this.filters.state !== 'Select All') ||
      this.filters.city ||
      (this.filters.mr && this.filters.mr !== 'Select All') ||
      (Array.isArray(this.filters.status) ? this.filters.status.length > 0 : (this.filters.status && this.filters.status !== 'Select All')) ||
      (this.filters.period && this.filters.period !== 'all')
    );
  }

  /** Returns true if the given filter key should be rendered */
  show(key: FilterKey): boolean {
    return this.visibleFilters.includes(key);
  }

  /**
   * Tailwind grid-cols class based on how many filters + reset button are shown.
   * This keeps the bar compact when fewer filters are visible.
   */
  get gridColClass(): string {
    return filtersGridClass(this.visibleFilters.length);
  }

  /** Fetched directly by id when restricted — the fixed set to search/filter within, client-side. */
  private allowedHqOptions: any[] = [];

  loadFilterOptions() {
    if (this.show('hq')) {
      if (this.allowedHqIds?.length) {
        this.fetchAllowedHqs();
      } else {
        this.fetchHqs('');
      }
    }

    if (this.show('mr')) {
      this.lookupService.getTelecallers().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        const list = res || [];
        this.mrOptions = [{ name: 'Select All', id: null }, ...list];
      });
    }
  }

  /**
   * Fetches exactly the allowed HQs by id, rather than paging/searching the org-wide
   * list and filtering after the fact — the allowed HQs aren't guaranteed to land on
   * page 1 of an unfiltered/unrelated search, which left the dropdown empty.
   */
  private fetchAllowedHqs() {
    const ids = this.allowedHqIds || [];
    forkJoin(ids.map((id) => this.hqService.getHqById(id).pipe(catchError(() => of(null)))))
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        this.allowedHqOptions = results.filter((hq): hq is NonNullable<typeof hq> => !!hq);
        this.hqOptions = [{ name: 'Select All', id: null }, ...this.allowedHqOptions];
      });
  }

  fetchHqs(term: string = '') {
    this.hqService.getHqs(1, 100, term).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      const list = res.headquarters || [];
      this.hqOptions = [{ name: 'Select All', id: null }, ...list];
    });
  }

  private hqSearchTimer: ReturnType<typeof setTimeout> | null = null;

  onHqSearch(event: { term: string, items: any[] }) {
    if (this.hqSearchTimer) clearTimeout(this.hqSearchTimer);
    this.hqSearchTimer = setTimeout(() => {
      if (this.allowedHqIds?.length) {
        // Restricted — filter the fixed allowed set client-side, don't hit the org-wide search.
        const term = event.term.trim().toLowerCase();
        const matches = term
          ? this.allowedHqOptions.filter((hq) => (hq.name || '').toLowerCase().includes(term))
          : this.allowedHqOptions;
        this.hqOptions = [{ name: 'Select All', id: null }, ...matches];
      } else {
        this.fetchHqs(event.term);
      }
    }, 400);
  }

  onHqClear() {
    if (this.hqSearchTimer) clearTimeout(this.hqSearchTimer);
    if (this.allowedHqIds?.length) {
      this.hqOptions = [{ name: 'Select All', id: null }, ...this.allowedHqOptions];
      return;
    }
    this.fetchHqs('');
  }

  onHqOpen() {
    if (!this.show('hq')) return;
    if (this.hqOptions.length <= 1 || (this.hqOptions.length === 2 && this.filters?.hq)) {
      if (this.allowedHqIds?.length) {
        this.fetchAllowedHqs();
      } else {
        this.fetchHqs('');
      }
    }
  }

  onMrOpen() {
    if (!this.show('mr')) return;
    if (this.mrOptions.length <= 1 || (this.mrOptions.length === 2 && this.filters?.mr)) {
      this.lookupService.getTelecallers().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        const list = res || [];
        this.mrOptions = [{ name: 'Select All', id: null }, ...list];
      });
    }
  }

  onStateOpen() {
    if (this.stateOptions.length <= 1) {
      this.loadStateOptions();
    }
  }

  loadStateOptions() {
    this.googleMapsLocationService.getStates().subscribe(states => {
      const stateList = states && states.length > 0 ? states.sort() : [];
      this.stateOptions = ['Select All', ...stateList];
    });
  }

  private cityTimer: ReturnType<typeof setTimeout> | null = null;

  onCityInputChange() {
    if (this.cityTimer) clearTimeout(this.cityTimer);
    this.cityTimer = setTimeout(() => {
      this.onFilterChange();
    }, 400);
  }

  onFilterChange() {
    this.normalizeFilters();
    this.filtersChange.emit(this.filters);
    this.filterChange.emit();
  }

  /** When state changes, clear city so stale values aren't sent to the API */
  onStateFilterChange() {
    this.filters.city = null;
    this.onFilterChange();
  }

  onPeriodChange() {
    const now = new Date();
    const formatDate = (d: Date) => d.toISOString().split('T')[0];

    if (!this.filters.period || this.filters.period === 'all') {
      this.filters.start_date = null;
      this.filters.end_date = null;
    } else if (this.filters.period === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      this.filters.start_date = formatDate(start);
      this.filters.end_date = formatDate(end);
    } else if (this.filters.period === 'last_3_months') {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      this.filters.start_date = formatDate(start);
      this.filters.end_date = formatDate(now);
    } else if (this.filters.period === 'last_6_months') {
      const start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      this.filters.start_date = formatDate(start);
      this.filters.end_date = formatDate(now);
    } else if (this.filters.period === 'this_year') {
      const start = new Date(now.getFullYear(), 0, 1);
      this.filters.start_date = formatDate(start);
      this.filters.end_date = formatDate(now);
    }
    this.onFilterChange();
  }

  resetFilters() {
    this.filters = {
      zone: null,
      hq: null,
      state: null,
      city: null,
      mr: null,
      period: null,
      start_date: null,
      end_date: null
    };
    this.onFilterChange();
  }
}
